import { Switch } from "@headlessui/react";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";

export default function Buyer() {
  const [people, setPeople] = useState([ ]);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBuyer, setNewBuyer] = useState({
    name: "",
    email: "",
    status: false
  });
  const [successMessage, setSuccessMessage] = useState('');

  // Add API base URL
  const API_URL = "https://didrouteapi.com/api/buyers";

  // Fetch buyers on component mount
  useEffect(() => {
    fetchBuyers();
  }, []);

  // Fetch buyers from API
  const fetchBuyers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      // Convert status string to boolean
      const buyersWithBooleanStatus = data.data.map(buyer => ({
        ...buyer,
        status: buyer.status === 'active'
      }));
      setPeople(buyersWithBooleanStatus || []);
    } catch (error) {
      console.error('Error fetching buyers:', error);
    }
  };

  // Handler functions moved inside component
  const handleStatusToggle = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update local state based on the returned status
        setPeople(prevPeople =>
          prevPeople.map(person =>
            person.id === id ? { ...person, status: data.status === 'active' } : person
          )
        );

        // Update campaign_targets status
        const targetResponse = await fetch(`${API_URL}/${id}/update-targets-status`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            buyer_id: id,
            status: data.status === 'active'
          })
        });

        if (!targetResponse.ok) {
          console.error('Failed to update campaign targets status');
        }
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this buyer?")) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          setPeople(people.filter((person) => person.id !== id));
        }
      } catch (error) {
        console.error('Error deleting buyer:', error);
      }
    }
  };

  const handleEdit = (person) => {
    setEditingId(person.id);
    setEditForm({
      id: person.id,
      name: person.name,
      email: person.email,
      status: person.status,
      createdAt: person.createdAt
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/${editingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editForm.name,
          email: editForm.email,
          status: editForm.status
        })
      });
      
      if (response.ok) {
        const updatedData = await response.json();
        setPeople(prevPeople => 
          prevPeople.map(person => 
            person.id === editingId ? { ...person, ...editForm } : person
          )
        );
        setSuccessMessage('Buyer updated successfully!');
        setTimeout(() => {
          setSuccessMessage('');
          setEditingId(null);
        }, 2000);
      } else {
        console.error('Failed to update buyer');
        setSuccessMessage('Failed to update buyer');
      }
    } catch (error) {
      console.error('Error updating buyer:', error);
      setSuccessMessage('Error updating buyer');
    }
  };

  const handleSearch = async (e) => {
    setSearchQuery(e.target.value);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/search?query=${e.target.value}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPeople(data.data || []);
      }
    } catch (error) {
      console.error('Error searching buyers:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const filteredPeople = people.filter(person =>
    person.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    person.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  const handleCreateBuyer = async () => {
    try {
      if (!newBuyer.name || !newBuyer.email) {
        setSuccessMessage('Please fill in all required fields');
        return;
      }

      const generatedPassword = generatePassword();
      const token = localStorage.getItem('token');
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newBuyer.name,
          email: newBuyer.email,
          password: generatedPassword,
          status: newBuyer.status
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Add the new buyer to the state with the generated password temporarily
        const newBuyerWithPlainPassword = {
          ...data.data,
          plainPassword: generatedPassword
        };
        setPeople([...people, newBuyerWithPlainPassword]);
        setNewBuyer({ name: "", email: "", status: false });
        setShowCreateModal(false);
        
        setSuccessMessage(data.message || 'Buyer created successfully!');
        
        // Remove plain password after 5 minutes
        setTimeout(() => {
          setPeople(currentPeople => 
            currentPeople.map(person => {
              if (person.id === newBuyerWithPlainPassword.id) {
                const { plainPassword, ...personWithoutPlainPassword } = person;
                return personWithoutPlainPassword;
              }
              return person;
            })
          );
        }, 300000); // 5 minutes
      } else {
        setSuccessMessage(data.error || 'Failed to create buyer');
      }
    } catch (error) {
      console.error('Error creating buyer:', error);
      setSuccessMessage('Error creating buyer');
    }
  };

  // Add Edit Modal JSX
  const EditModal = () => {
    if (!editingId) return null;
    
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 overflow-y-auto">
        <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
          <h2 className="text-lg font-semibold text-black mb-4">Edit Buyer</h2>

          {successMessage && (
            <div className="mb-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded">
              {successMessage}
            </div>
          )}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-1">
              Name
            </label>
            <input
              id="name"
              type="text"
              name="name"
              placeholder="Name"
              value={editForm.name || ""}
              onChange={handleEditChange}
              className="block w-full rounded-md border-0 bg-gray-200 py-1.5 px-3 text-gray-900 placeholder:text-gray-900 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              autoComplete="off"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              placeholder="Email"
              value={editForm.email || ""}
              onChange={handleEditChange}
              className="block w-full rounded-md border-0 bg-gray-200 py-1.5 px-3 text-gray-900 placeholder:text-gray-900 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              autoComplete="off"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setEditingId(null);
                setSuccessMessage("");
              }}
              className="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            <div className="sm:flex sm:items-center sm:justify-between">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Buyer
                </h1>
              </div>
              <div className="flex gap-4 items-center mt-4 sm:mt-0">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search buyers..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="block w-full border border-gray-700 rounded-md py-1.5 px-3 text-gray-900 placeholder:text-gray-900 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-indigo-500"
                >
                  Create Buyer
                </button>
                <button
                  onClick={() =>
                    (window.location.href = "https://didroute.com/buyer/login")
                  }
                  target="_blank"
                  className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-indigo-500"
                >
                  Login
                </button>
              </div>
            </div>
            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-black sm:pl-0"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Email
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Password
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Created At
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-xs font-medium text-black"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {filteredPeople.map((person, index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                            {person.name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                            {person.email}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                            {person.plainPassword ? (
                              <div className="flex items-center">
                                <span className="text-green-600 font-medium">
                                  {person.plainPassword}
                                </span>
                                <span className="ml-2 text-xs text-gray-500">
                                  (visible temporarily)
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-500">●●●●●●●●</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                            {formatDate(person.created_at)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                            <span
                              className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                                person.status
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {person.status ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-left text-xs font-medium sm:pr-0">
                            <div className="flex items-center space-x-3">
                              <Switch
                                checked={person.status}
                                onChange={() => handleStatusToggle(person.id)}
                                className={`${
                                  person.status ? "bg-green-600" : "bg-gray-600"
                                } relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none`}
                              >
                                <span className="sr-only">Toggle status</span>
                                <span
                                  className={`${
                                    person.status
                                      ? "translate-x-4"
                                      : "translate-x-1"
                                  } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                                />
                              </Switch>

                              {/*  */}
                              <button
                                onClick={() => handleEdit(person)}
                                className="text-indigo-400 hover:text-indigo-300"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              {/* delete button */}
                              <button
                                onClick={() => handleDelete(person.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Create Buyer Modal */}
            {showCreateModal && (
              <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 overflow-y-auto">
                <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                  <h2 className="text-lg font-semibold text-black mb-4">
                    Create New Buyer
                  </h2>

                  {successMessage && (
                    <div
                      className={`mb-4 p-2 rounded ${
                        successMessage.includes("Error") ||
                        successMessage.includes("Failed")
                          ? "bg-red-100 border border-red-400 text-red-700"
                          : "bg-green-100 border border-green-400 text-green-700"
                      }`}
                    >
                      {successMessage}
                    </div>
                  )}

                  <div className="mb-4">
                    <label
                      htmlFor="createName"
                      className="block text-sm font-medium text-gray-900 mb-1"
                    >
                      Name *
                    </label>
                    <input
                      id="createName"
                      type="text"
                      placeholder="Name"
                      value={newBuyer.name}
                      onChange={(e) =>
                        setNewBuyer({ ...newBuyer, name: e.target.value })
                      }
                      className="block w-full rounded-md border-0 bg-gray-200 py-1.5 px-3 text-gray-900 placeholder:text-gray-500 focus:ring-2 sm:text-sm sm:leading-6"
                      autoComplete="off"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="createEmail"
                      className="block text-sm font-medium text-gray-900 mb-1"
                    >
                      Email *
                    </label>
                    <input
                      id="createEmail"
                      type="email"
                      placeholder="Email"
                      value={newBuyer.email}
                      onChange={(e) =>
                        setNewBuyer({ ...newBuyer, email: e.target.value })
                      }
                      className="block w-full rounded-md border-0 bg-gray-200 py-1.5 px-3 text-gray-900 placeholder:text-gray-500 sm:text-sm sm:leading-6"
                      autoComplete="off"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowCreateModal(false);
                        setSuccessMessage("");
                        setNewBuyer({ name: "", email: "", status: false });
                      }}
                      className="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateBuyer}
                      className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add this before the closing div */}
      {editingId && <EditModal />}
    </div>
  );
}
